// public/js/modules/map/constants.js

export const typeColors = {
  'engine-stand': '#0d6efd',
  'apu': '#fd7e14',
  'other': '#6c757d',
  'default': '#198754'
};

/**
 * Validates a lat/lon pair. Rejects null, non-numeric (e.g. "" → NaN) and
 * out-of-range values. A single bad coordinate slipping into fitBounds()
 * sends the camera to NaN/extreme positions (the "jumps to the north pole and
 * can't be panned" bug), so every coordinate entering the map is checked.
 */
export function isValidLatLon(lat, lon) {
  const la = parseFloat(lat);
  const lo = parseFloat(lon);
  return Number.isFinite(la) && Number.isFinite(lo) &&
         la >= -90 && la <= 90 && lo >= -180 && lo <= 180 &&
         !(la === 0 && lo === 0); // treat 0/0 "null island" as invalid
}

// Default style for MapLibre beta.
//
// We use OpenFreeMap ("liberty" = detailed OSM streets, labels, POIs) as the
// zero-config default: fully hosted, NO API key required, free for commercial
// use. This replaces the old demotiles.maplibre.org style, which is only a
// "hello world" world-borders demo with almost no detail.
//
// Other OpenFreeMap styles you can drop in (no key): "bright", "positron" (light/grey).
//
// --- Upgrade paths (set window.APP_CONFIG.MAPLIBRE_STYLE_URL, ideally injected
//     server-side like GOOGLE_MAPS_API_KEY) ---
//   • MapTiler (free tier, needs key) — streets / outdoor / dataviz / hybrid (satellite):
//       https://api.maptiler.com/maps/streets-v2/style.json?key=YOUR_KEY
//       https://api.maptiler.com/maps/hybrid/style.json?key=YOUR_KEY   (satellite + labels)
//   • Self-hosted PMTiles (single file on S3/Lambda, no tile server, no per-request cost)
//     — best long-term option for a commercial tracking product. See pmtiles + protomaps.
export const DEFAULT_MAPLIBRE_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

// Glyphs (fonts) endpoint used for our own symbol layers (cluster counts, asset
// labels, route arrows). OpenFreeMap serves "Noto Sans Regular/Bold/Italic".
// We reference this in the raster styles we build so labels render on top of
// raster base maps too (raster tiles ship no glyphs of their own).
export const GLYPHS_URL = 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf';

// Font that exists in ALL our base maps (OpenFreeMap vector styles use Noto Sans,
// and our raster styles point GLYPHS_URL at the same font server). Use this for
// every symbol layer we add, otherwise MapLibre falls back to "Open Sans" which
// the OpenFreeMap glyph server does not provide → labels silently disappear.
export const LABEL_FONT = ['Noto Sans Regular'];

// Below this many markers the map keeps the RICH DOM markers instead of
// switching to the cluster layer: a "cluster" of two or three is less use than
// the labelled pins it replaces, and stacked pins are now fanned out by
// spreadCoLocatedMarkers() instead. It lives here because THREE places need the
// same number - the renderer, applyClustering() and the toolbar button - and
// the first two disagreeing is how the button came to flip to green and change
// nothing (reported 2026-10-16).
export const CLUSTER_MIN_ASSETS = 4;

// === Selectable base layers (all free / no API key required) ===
// type 'vector' → full vector style URL (best detail/labels)
// Esri World Imagery. With a key we use the licensed, metered endpoint; without
// one we fall back to the open arcgisonline host, which works but is outside
// Esri's terms for a commercial product (docs/maps.md). Keyed is the only
// version that should ever reach a customer.
export function esriImageryTiles() {
  const key = (typeof window !== 'undefined' && window.APP_CONFIG && window.APP_CONFIG.ESRI_API_KEY) || '';
  return key
    ? `https://ibasemaps-api.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}?token=${key}`
    : 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
}

// type 'raster' → XYZ raster tiles; we wrap them in a minimal style at runtime
//
// `label` is ONE word where possible: a native select sizes its drop-down to
// the widest option, so long labels made the open list far wider than the
// closed field. The provider belongs in `provider` (shown as the option's
// hover) and in the attribution the map already prints.
export const BASE_LAYERS = [
  {
    id: 'liberty',
    label: 'Streets',
    provider: 'OpenFreeMap Liberty (vector)',
    type: 'vector',
    style: 'https://tiles.openfreemap.org/styles/liberty'
  },
  {
    id: 'bright',
    label: 'Bright',
    provider: 'OpenFreeMap Bright (vector)',
    type: 'vector',
    style: 'https://tiles.openfreemap.org/styles/bright'
  },
  {
    id: 'positron',
    label: 'Light',
    provider: 'OpenFreeMap Positron (vector)',
    type: 'vector',
    style: 'https://tiles.openfreemap.org/styles/positron'
  },
  {
    id: 'dark',
    label: 'Dark',
    provider: 'OpenFreeMap Dark (vector)',
    type: 'vector',
    style: 'https://tiles.openfreemap.org/styles/dark'
  },
  {
    id: 'satellite',
    label: 'Satellite',
    provider: 'Esri World Imagery',
    type: 'raster',
    // LICENCE WARNING (docs/maps.md). This unauthenticated arcgisonline.com
    // endpoint is NOT licensed for commercial use - Esri staff state that in
    // writing, and the terms say an ArcGIS subscription is required without
    // Esri software. The fix is an ArcGIS Location Platform key and the
    // ibasemaps-api.arcgis.com host: 2M tiles/month free, then $0.15/1000.
    // Set window.APP_CONFIG.ESRI_API_KEY and this switches over by itself.
    tiles: [esriImageryTiles()],
    tileSize: 256,
    maxzoom: 19,
    // Esri's current credit string for this item - Maxar was renamed Vantor.
    attribution: 'Imagery © Esri, Vantor, Earthstar Geographics, and the GIS User Community'
  },
  {
    id: 'topo',
    label: 'Topographic',
    provider: 'OpenTopoMap',
    type: 'raster',
    tiles: [
      'https://a.tile.opentopomap.org/{z}/{x}/{y}.png',
      'https://b.tile.opentopomap.org/{z}/{x}/{y}.png',
      'https://c.tile.opentopomap.org/{z}/{x}/{y}.png'
    ],
    tileSize: 256,
    maxzoom: 17,
    // Verbatim as OpenTopoMap's terms require it - their wording, not a
    // paraphrase, and it has to stay visible (docs/maps.md).
    attribution: 'Kartendaten: © OpenStreetMap-Mitwirkende, SRTM | '
      + 'Kartendarstellung: © OpenTopoMap (CC-BY-SA)'
  },
  {
    id: 'osm',
    label: 'OpenStreetMap',
    provider: 'OpenStreetMap raster tiles',
    type: 'raster',
    tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 19,
    attribution: '© OpenStreetMap contributors'
  }
];
